import is from '@sindresorhus/is';
import type { Maybe } from 'true-myth';
import { Result } from 'true-myth';
import type { SetRequired } from 'type-fest';
import { z } from 'zod';

import type {
    JobArchitecture,
    JobArchitectureDebugInformation,
    JobArchitectureFiltersResponse,
    JobArchitectureInflight,
    JobArchitectureMetadata,
    JobArchitectureOnboardingState,
    JobArchitectureOptionsResponse,
    JobArchitecturePath,
} from '~/shared/data-contract/job-architecture';
import {
    jobArchitectureDebugInfoSchema,
    jobArchitectureFiltersResponseSchema,
    jobArchitectureInflightSchema,
    jobArchitectureOnboardingStateSchema,
    jobArchitectureOptionsResponseSchema,
    jobArchitecturePathSchema,
    jobArchitectureSchema,
    jobArchitecturesSchema,
    nullableJobArchitectureMetadataSchema,
} from '~/shared/data-contract/job-architecture';
import type { CommentStatistics } from '~/shared/data-contract/job-architecture-comment-statistics';
import { commentStatisticsSchema } from '~/shared/data-contract/job-architecture-comment-statistics';
import type { ContributionTimeline } from '~/shared/data-contract/job-architecture-contribution-timeline';
import { contributionTimelineSchema } from '~/shared/data-contract/job-architecture-contribution-timeline';
import type { PaginatedJobArchitectureHistory } from '~/shared/data-contract/job-architecture-history';
import { paginatedJobArchitectureHistorySchema } from '~/shared/data-contract/job-architecture-history';
import {
    type JobArchitectureSearchResponseWithDetail,
    jobArchitectureSearchResponseWithDetailSchema,
} from '~/shared/data-contract/job-architecture-search';
import type { JobArchitectureSkillLimits } from '~/shared/data-contract/job-architecture-skill-limits';
import { jobArchitectureSkillLimitsSchema } from '~/shared/data-contract/job-architecture-skill-limits';
import type { CareerTrack } from '~/shared/data-contract/job-architecture-trade-union';
import { createCareerTrackWithTradeUnionsSchema } from '~/shared/data-contract/job-architecture-trade-union';
import type { JobCluster } from '~/shared/data-contract/job-cluster';
import { jobClusterWithTypeSchemaTransformed } from '~/shared/data-contract/job-cluster';
import type { FamilyWithClusters, JobFamily } from '~/shared/data-contract/job-family';
import { familyWithClustersSchema, jobFamilyWithTypeSchemaTransformed } from '~/shared/data-contract/job-family';
import type { JobLevel, JobLevelWithReviewer } from '~/shared/data-contract/job-level';
import { jobLevelWithTypeSchemaTransformed } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import { jobLevelDetailsSchema } from '~/shared/data-contract/job-level-details';
import type {
    Competencies,
    JobProfileComponent,
    JobProfileForGenerationWithPrompt,
    JobProfileLegacy,
} from '~/shared/data-contract/job-profile';
import {
    competenciesSchema,
    type JobProfileRegenerationResponse,
    jobProfileLegacySchema,
    jobProfileRegenerationResponseSchema,
} from '~/shared/data-contract/job-profile';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import { jobProfileVersionsSchema } from '~/shared/data-contract/job-profile-versions';
import type { JobRole, JobRoleWithReviewer } from '~/shared/data-contract/job-role';
import { jobRoleWithTypeSchemaTransformed } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';
import { jobRoleDetailsSchema } from '~/shared/data-contract/job-role-details';
import type { LevelingFramework, PaginatedLevelingFramework } from '~/shared/data-contract/leveling-framework';
import { levelingFrameworkSchema, paginatedLevelingFrameworkSchema } from '~/shared/data-contract/leveling-framework';
import type {
    CreateLevelingFrameworkPayload,
    UpdateLevelingFrameworkPayload,
} from '~/shared/data-contract/leveling-framework-payloads';
import type { Persons } from '~/shared/data-contract/person';
import { personsSchema } from '~/shared/data-contract/person';
import type {
    PaginatedSkillCatalog,
    SkillCatalogOptions,
    SkillCatalogStatistics,
    SkillReferences,
    SkillShort,
    SkillSyncStatus,
    SkillType,
} from '~/shared/data-contract/skill';
import {
    paginatedSkillCatalogSchema,
    skillCatalogOptionsSchema,
    skillCatalogStatisticsSchema,
    skillReferencesSchema,
    skillShortSchema,
} from '~/shared/data-contract/skill';
import type { SkillDetails } from '~/shared/data-contract/skill-details';
import { skillDetailsSchema } from '~/shared/data-contract/skill-details';
import type { PaginatedUsers } from '~/shared/data-contract/user';
import { userPaginationSchema } from '~/shared/data-contract/user';
import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError, Pathname } from '~/shared/http-fetcher/http-fetcher';
import type { LanguageCode, LanguageCodeForJSA } from '~/shared/internationalization';
import type { Translate } from '~/shared/internationalization/translation';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type { CompanyInformationGenerationResponse } from '../../data-contract/company-information-generation';
import { companyInformationGenerationResponseSchema } from '../../data-contract/company-information-generation';
import type {
    CustomJobProfileTemplateRequest,
    CustomJobProfileTemplateResponse,
    ManagedJobProfileTemplateResponse,
} from '../../data-contract/job-profile-template';
import {
    customJobProfileTemplateGetResponseSchema,
    customJobProfileTemplateResponseSchema,
    managedJobProfileTemplateResponseArraySchema,
} from '../../data-contract/job-profile-template';
import {
    createJobProfileLegacyUpdatePayload,
    createJobProfileUpdatePayload,
    type JobProfileUpdatePayload,
} from '../../data-contract/job-profile-update';
import type {
    LevelingFrameworkAssessmentResponse,
    LevelingFrameworkGenerationResponse,
} from '../../data-contract/leveling-framework-assessment';
import {
    levelingFrameworkAssessmentResponseSchema,
    levelingFrameworkGenerationResponseSchema,
} from '../../data-contract/leveling-framework-assessment';
import type { RegenerationCommentsContext } from '../../data-contract/regeneration-comments-context';
import type {
    TaskManagementClusterSearchRequest,
    TaskManagementClustersResponse,
    TaskManagementSearchRequestInput,
    TaskManagementTaskDetail,
    TaskManagementTasksResponse,
    WorkTaskAutomationRecommendation,
} from '../../data-contract/task-management';
import {
    taskManagementClustersResponseSchema,
    taskManagementTaskDetailSchema,
    taskManagementTasksResponseSchema,
    workTaskAutomationRecommendationSchema,
} from '../../data-contract/task-management';
import { applyOnboardingDraft } from '../../job-architecture-v2/api/apply-onboarding-draft';
import {
    type JsaRegenerationResult,
    type JsaStructureRegenerationResponse,
    jsaRegenerationResultSchema,
    jsaStructureRegenerationResponseSchema,
    type RegenerateOnboardingJsaDraft,
} from '../../job-architecture-v2/api/real-time/jsa-structure-regeneration-schema';
import {
    type FamiliesRegenerationResult,
    familiesRegenerationResultSchema,
} from '../../job-architecture-v2/api/use-families-regeneration-results';
import type { DraftFamilyRow } from '../../job-architecture-v2/onboarding/table/rows';
import type { RedetectSkillsRequestPayload, SkillsDetectionResult } from './redetect-skills-request';
import { redetectSkillsWithPromptSchema } from './redetect-skills-request';

type JsonFetcher = ValidatingHttpFetcherWithBaseUrl;

type SkillWithRequiredId = Partial<SetRequired<SkillShort, 'id'>>;

export type AssignableReviewersItem = {
    readonly id: string;
    readonly architectureId: string;
    readonly type: 'level' | 'role';
};

export type InviteAndAssignRequest = {
    readonly method?: 'EMAIL' | 'SSO';
    readonly emailAddress: string;
    readonly firstName: string;
    readonly inviteLanguage?: string;
    readonly jobTitle?: string;
    readonly lastName: string;
    readonly roles?: readonly string[];
};

export type AssignReviewerItem = JobLevelDetails | JobLevelWithReviewer | JobRoleDetails | JobRoleWithReviewer;

export type TitleAndDescription = {
    readonly description: string;
    readonly title: string;
};

const downloadLinkResponseSchema = z.object({
    downloadLink: z.string(),
});

export type DownloadLinkResponse = z.infer<typeof downloadLinkResponseSchema>;

const queuedExportResponseSchema = z.object({
    exportId: z.string(),
});

export type ChangelogExportResponse = z.infer<typeof queuedExportResponseSchema>;
export type JobArchitectureExportResponse = z.infer<typeof queuedExportResponseSchema>;
export type SkillCatalogExportResponse = z.infer<typeof queuedExportResponseSchema>;

export type UserRole = 'HIRING_MANAGER' | 'JOB_ARCHITECT' | 'RECRUITER';

export type GetUsersParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
    readonly role?: UserRole;
    readonly sort?: string;
};

export type SkillSourceFilter = 'attributed' | 'manual';

export type GetJobArchitectureSkillsParams = {
    readonly jobArchitectureIds?: readonly string[];
    readonly levelIds?: readonly string[];
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
    readonly roleIds?: readonly string[];
    readonly searchTerm?: string;
    readonly skillTypes?: readonly string[];
    readonly sort?: string;
    readonly sources?: readonly SkillSourceFilter[];
    readonly statusFilter?: readonly string[];
};

export type GetJobArchitectureHistoryParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
};

export type GetLevelingFrameworksParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
};

export type JobArchitectureFetchers = {
    readonly addCareerTrack: (args: {
        readonly careerTrack: Omit<CareerTrack, 'id' | 'trackType'>;
        readonly jobArchitectureId: string;
    }) => Promise<Result<CareerTrack, HttpFetcherError>>;
    readonly archiveJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly archiveJobLevels: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly archiveJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly archiveJobRoles: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly assignDefaultLevelingFramework: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly assignReviewer: (args: {
        readonly item: AssignReviewerItem;
    }) => Promise<Result<JobLevel | JobRole, HttpFetcherError>>;
    readonly bulkApplyOnboardingDraft: (args: {
        readonly draft: readonly DraftFamilyRow[];
        readonly jobArchitectureId: string;
        readonly languageCode: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly copyJobClusters: (args: {
        readonly jobClusters: NonEmptyArray<JobCluster>;
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly copyJobLevels: (args: {
        readonly jobLevels: NonEmptyArray<JobLevel>;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly copyJobRoles: (args: {
        readonly jobClusterId: string;
        readonly jobRoles: NonEmptyArray<JobRole>;
    }) => Promise<Result<unknown, Error>>;
    readonly createJobArchitecture: () => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly createJobArchitectureLevelingFramework: (args: {
        readonly jobArchitectureId: string;
        readonly payload: CreateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly createLevelingFramework: (args: {
        readonly payload: CreateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly createSkillCatalogEntry: (args: {
        readonly skillId: string;
        readonly syncStatus: SkillSyncStatus;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly requestSkillCatalogExport: (args: {
        readonly onlyExternallySynced: boolean;
    }) => Promise<Result<SkillCatalogExportResponse, HttpFetcherError>>;
    readonly deleteJobArchitecture: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly linkJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly deleteJobClusters: (jobClusters: NonEmptyArray<JobCluster>) => Promise<Result<unknown, Error>>;
    readonly deleteJobFamilies: (jobFamilies: NonEmptyArray<JobFamily>) => Promise<Result<unknown, Error>>;
    readonly deleteJobLevels: (
        jobLevels: NonEmptyArray<Pick<JobLevel, 'architectureId' | 'id'>>,
    ) => Promise<Result<unknown, Error>>;
    readonly deleteJobRoles: (
        jobRoles: NonEmptyArray<Pick<JobRole, 'architectureId' | 'id'>>,
    ) => Promise<Result<unknown, Error>>;
    readonly deleteLevelingFramework: (args: {
        readonly frameworkId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteSkillCatalogEntry: (args: {
        readonly skillId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly editJobLevelVersionNote: (args: {
        readonly comment: string | null;
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly editJobRoleVersionNote: (args: {
        readonly comment: string | null;
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly exportJobArchitecture: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
    }) => Promise<Result<JobArchitectureExportResponse, HttpFetcherError>>;
    readonly requestChangelogExport: (args: {
        readonly jobArchitectureId: string;
        readonly dateFrom?: string;
        readonly dateTo?: string;
        readonly familyOrClusterId?: string;
        readonly recipientEmail?: string;
    }) => Promise<Result<ChangelogExportResponse, HttpFetcherError>>;
    readonly exportJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly languageCode: string;
        readonly version?: number;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly exportJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly languageCode: string;
        readonly version?: number;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly fetchArchivedJobLevels: (args: {
        readonly includeProfiles?: boolean;
        readonly jobArchitectureId: string;
    }) => Promise<Result<readonly JobLevel[], HttpFetcherError>>;
    readonly fetchArchivedJobRoles: (args: {
        readonly includeProfiles?: boolean;
        readonly jobArchitectureId: string;
    }) => Promise<Result<readonly JobRole[], HttpFetcherError>>;
    readonly fetchAssignableReviewers: (args: {
        readonly abortSignal?: AbortSignal;
        readonly jobRoleOrJobLevel: AssignableReviewersItem;
        readonly searchTerm?: string;
    }) => Promise<Result<Persons, HttpFetcherError>>;
    readonly fetchCareerTracks: (
        jobArchitectureId: string,
    ) => Promise<Result<readonly CareerTrack[], HttpFetcherError>>;
    readonly fetchCompanyInformationGenerationStatus: (args: {
        readonly jobArchitectureId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<CompanyInformationGenerationResponse, HttpFetcherError>>;
    readonly fetchCompetencies: () => Promise<Result<Competencies, HttpFetcherError>>;
    readonly fetchLevelingFrameworkAssessment: (args: {
        readonly assessmentId: string;
        readonly jobArchitectureId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<LevelingFrameworkAssessmentResponse, HttpFetcherError>>;
    readonly fetchCommentStatistics: (args: {
        readonly from?: string;
        readonly jobArchitectureId: string;
        readonly language: LanguageCode;
        readonly to?: string;
    }) => Promise<Result<CommentStatistics, HttpFetcherError>>;
    readonly fetchContributionTimeline: (args: {
        readonly days?: number;
        readonly jobArchitectureId: string;
        readonly timezone: string;
    }) => Promise<Result<ContributionTimeline, HttpFetcherError>>;
    readonly fetchDebugInformation: (
        item: JobCluster | JobFamily | JobLevel | JobRole,
    ) => Promise<Result<JobArchitectureDebugInformation, HttpFetcherError>>;
    readonly fetchFamiliesWithClusters: (
        architectureId: string,
        options?: { readonly acceptLanguage?: LanguageCode; readonly signal?: AbortSignal },
    ) => Promise<Result<readonly FamilyWithClusters[], HttpFetcherError>>;
    readonly fetchJobArchitecture: (
        jobArchitectureId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly fetchJobArchitectureFilters: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitectureFiltersResponse, HttpFetcherError>>;
    readonly fetchJobArchitectureHistory: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobArchitectureId: string;
        readonly params: GetJobArchitectureHistoryParams;
    }) => Promise<Result<PaginatedJobArchitectureHistory, HttpFetcherError>>;
    readonly fetchJobArchitectureInflight: (
        jobArchitectureId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<JobArchitectureInflight, HttpFetcherError>>;
    readonly fetchJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly fetchJobArchitectureLevelingFrameworks: (args: {
        readonly jobArchitectureId: string;
        readonly params: GetLevelingFrameworksParams;
    }) => Promise<Result<PaginatedLevelingFramework, HttpFetcherError>>;
    readonly fetchJobArchitectureMetadata: (
        jobArchitectureId: string,
    ) => Promise<Result<JobArchitectureMetadata | null, HttpFetcherError>>;
    readonly fetchJobArchitectureOnboardingState: (
        jobArchitectureId: string,
    ) => Promise<Result<JobArchitectureOnboardingState, HttpFetcherError>>;
    readonly fetchJobArchitectureOptions: () => Promise<Result<JobArchitectureOptionsResponse, HttpFetcherError>>;
    readonly fetchJobArchitectures: () => Promise<Result<readonly JobArchitecture[], HttpFetcherError>>;
    readonly fetchJobArchitectureSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly params: GetJobArchitectureSkillsParams;
    }) => Promise<Result<PaginatedSkillCatalog, HttpFetcherError>>;
    readonly fetchJobClusters: (
        architectureId: string,
        familyId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobCluster[], HttpFetcherError>>;
    readonly fetchJobFamilies: (
        architectureId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobFamily[], HttpFetcherError>>;
    readonly fetchJobLevels: (
        architectureId: string,
        roleId: string,
        options?: { readonly acceptLanguage?: LanguageCode; readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobLevel[], HttpFetcherError>>;
    readonly fetchJobLevelVersions: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<JobProfileVersions, HttpFetcherError>>;
    readonly fetchJobRoles: (
        architectureId: string,
        clusterId: string,
        options?: { readonly acceptLanguage?: LanguageCode; readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobRole[], HttpFetcherError>>;
    readonly fetchJobRoleVersions: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<JobProfileVersions, HttpFetcherError>>;
    readonly fetchLevelingFramework: (args: {
        readonly frameworkId: string;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly fetchLevelingFrameworks: (args: {
        readonly params: GetLevelingFrameworksParams;
    }) => Promise<Result<PaginatedLevelingFramework, HttpFetcherError>>;
    readonly fetchRelatedSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly skills: readonly SkillWithRequiredId[];
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly fetchSkillDetails: (args: {
        readonly language: string;
        readonly skillId: string;
    }) => Promise<Result<SkillDetails, HttpFetcherError>>;
    readonly fetchSkillLimits: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitectureSkillLimits, HttpFetcherError>>;
    readonly fetchSkillReferences: (args: {
        readonly language: string;
        readonly skillId: string;
    }) => Promise<Result<SkillReferences, HttpFetcherError>>;
    readonly fetchSkillCatalogOptions: () => Promise<Result<SkillCatalogOptions, HttpFetcherError>>;
    readonly fetchSkillCatalogStatistics: (args: {
        readonly jobArchitectureIds?: readonly string[];
        readonly levelIds?: readonly string[];
        readonly roleIds?: readonly string[];
        readonly searchTerm?: string;
        readonly skillTypes?: readonly SkillType[];
        readonly sources?: readonly SkillSourceFilter[];
        readonly statusFilter?: readonly SkillSyncStatus[];
    }) => Promise<Result<SkillCatalogStatistics, HttpFetcherError>>;
    readonly fetchSkillLocales: () => Promise<Result<readonly string[], HttpFetcherError>>;
    readonly fetchSkillsBySearchTerm: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly searchTerm: string;
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly fetchTaskManagementClusters: (args: {
        readonly architectureId: string;
        readonly searchTerm?: string;
    }) => Promise<Result<TaskManagementClustersResponse, HttpFetcherError>>;
    readonly searchTaskManagementClusterTasks: (args: {
        readonly architectureId: string;
        readonly params: TaskManagementClusterSearchRequest;
    }) => Promise<Result<TaskManagementTasksResponse, HttpFetcherError>>;
    readonly fetchTaskManagementTask: (args: {
        /** The language of the title and content; the browser's language decides when omitted. */
        readonly acceptLanguage?: LanguageCodeForJSA;
        readonly taskId: string;
        readonly version?: number;
    }) => Promise<Result<TaskManagementTaskDetail, HttpFetcherError>>;
    readonly fetchWorkTaskAutomationRecommendation: (args: {
        readonly taskId: string;
    }) => Promise<Result<WorkTaskAutomationRecommendation, HttpFetcherError>>;
    readonly cancelJobArchitectureGenerations: () => Promise<Result<unknown, Error>>;
    readonly generateJobArchitectureWithAI: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly awaitKnowledgeBaseIngestion?: boolean;
        readonly jobArchitectureId: string;
        readonly type: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobClustersWithAI: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobArchitectureId: string;
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobFamilyWithAI: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobLevelsWithAI: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly autoSelectTracks: boolean;
        readonly careerTracks: readonly string[];
        readonly createRoleCopy: boolean;
        readonly generateLevels: boolean;
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly respectSkillsFromRole: boolean;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobLevelsForAllRoles: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly createRoleCopy: boolean;
        readonly generateLevels: boolean;
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobRolesWithAI: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobArchitectureId: string;
        readonly jobClusterId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly getJobArchitectureItemPath: (args: {
        readonly itemId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitecturePath, HttpFetcherError>>;
    readonly getUsers: (args: GetUsersParams) => Promise<Result<PaginatedUsers, HttpFetcherError>>;
    readonly inviteAndAssignToLevel: (args: {
        readonly jobArchitectureId: string;
        readonly levelId: string;
        readonly request: InviteAndAssignRequest;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly inviteAndAssignToRole: (args: {
        readonly jobArchitectureId: string;
        readonly request: InviteAndAssignRequest;
        readonly roleId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly moveJobClusters: (args: {
        readonly jobClusters: readonly JobCluster[];
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly moveJobLevels: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevels: readonly JobLevel[];
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly moveJobRoles: (args: {
        readonly jobArchitectureId: string;
        readonly jobClusterId: string;
        readonly jobRoles: readonly JobRole[];
    }) => Promise<Result<unknown, Error>>;
    readonly reorderJobClusters: (args: {
        readonly itemIds: readonly string[];
        readonly jobArchitectureId: string;
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly reorderJobFamilies: (args: {
        readonly itemIds: readonly string[];
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly reorderJobLevels: (args: {
        readonly itemIds: readonly string[];
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly reorderJobRoles: (args: {
        readonly itemIds: readonly string[];
        readonly jobArchitectureId: string;
        readonly jobClusterId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly patchJobArchitecture: (args: {
        readonly jobArchitecture: JobArchitecture;
        readonly languageCode: string;
    }) => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly patchJobArchitectureMetadata: (args: {
        readonly jobArchitectureId: string;
        readonly metadata: Partial<JobArchitectureMetadata>;
        readonly allowJsaRename?: boolean;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly patchJobArchitectureOnboardingState: (args: {
        readonly jobArchitectureId: string;
        readonly onboardingState: Partial<JobArchitectureOnboardingState>;
    }) => Promise<Result<JobArchitectureOnboardingState, HttpFetcherError>>;
    readonly patchJobCluster: (args: {
        readonly jobCluster: JobCluster;
        readonly withAutoTranslation: boolean;
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly patchJobFamily: (args: {
        readonly jobFamily: JobFamily;
        readonly languageCode: string;
        readonly withAutoTranslation: boolean;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly patchJobLevel: (args: {
        readonly jobLevel: JobLevel;
        readonly languageCode: string;
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly patchJobRole: (args: {
        readonly jobRole: JobRole;
        readonly languageCode: string;
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly persistJobCluster: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly persistJobFamily: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly persistJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly persistJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly publishJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly publishJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unpublishJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unpublishJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly putJobCluster: (args: {
        readonly jobArchitectureId: string;
        readonly jobCluster: JobCluster;
        readonly jobFamilyId: string;
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly putJobFamily: (args: {
        readonly jobArchitectureId: string;
        readonly jobFamily: JobFamily;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly readJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly readJobLevelDetails: (args: {
        readonly includeComments?: boolean;
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly signal?: AbortSignal;
        readonly version: string | undefined;
    }) => Promise<Result<JobLevelDetails, HttpFetcherError>>;
    readonly readJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly readJobRoleDetails: (args: {
        readonly includeComments?: boolean;
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly signal?: AbortSignal;
        readonly version: string | undefined;
    }) => Promise<Result<JobRoleDetails, HttpFetcherError>>;
    readonly redetectSkills: (
        args: RedetectSkillsRequestPayload,
    ) => Promise<Result<SkillsDetectionResult, HttpFetcherError>>;
    readonly regenerateJobProfileDescription: (args: {
        readonly acceptLanguage: LanguageCodeForJSA;
        readonly additionalPrompt: Maybe<string>;
        readonly commentsContext?: RegenerationCommentsContext;
        readonly jobProfile: JobProfileComponent & JobProfileForGenerationWithPrompt;
    }) => Promise<Result<JobProfileRegenerationResponse, HttpFetcherError>>;
    readonly deleteFamiliesRegenerationResults: (args: {
        readonly jobArchitectureId: string;
        readonly regenerationId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchFamiliesRegenerationResults: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<FamiliesRegenerationResult, HttpFetcherError>>;
    readonly fetchRegenerationResult: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobArchitectureId: string;
        readonly regenerationId: string;
    }) => Promise<Result<JsaRegenerationResult, HttpFetcherError>>;
    readonly regenerateOnboarding: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobArchitectureId: string;
        readonly jsaDraft: RegenerateOnboardingJsaDraft;
        readonly prompt: string;
    }) => Promise<Result<JsaStructureRegenerationResponse, HttpFetcherError>>;
    readonly reviewJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly reviewJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly submitCompanyInformationGeneration: (args: {
        readonly jobArchitectureId: string;
        readonly payload: {
            readonly companyDomain: string | null;
            readonly companyName: string;
        };
        readonly regenerate: boolean;
        readonly signal?: AbortSignal;
    }) => Promise<Result<CompanyInformationGenerationResponse, HttpFetcherError>>;
    readonly submitLevelingFrameworkAssessment: (args: {
        readonly documentIds: readonly string[];
        readonly jobArchitectureId: string;
    }) => Promise<Result<LevelingFrameworkAssessmentResponse, HttpFetcherError>>;
    readonly submitLevelingFrameworkGeneration: (args: {
        readonly jobArchitectureId: string;
        readonly payload: {
            readonly assessmentId?: string;
            readonly documentIds: readonly string[];
            readonly levelCriteria: readonly { readonly label: string; readonly option: string }[];
            readonly name: string | null;
            readonly tracks: readonly {
                readonly levelCount?: number;
                readonly levelNumbers?: readonly number[];
                readonly levelTitles: readonly string[];
                readonly name: string;
            }[];
        };
    }) => Promise<Result<LevelingFrameworkGenerationResponse, HttpFetcherError>>;
    readonly fetchLevelingFrameworkGeneration: (args: {
        readonly generationId: string;
        readonly jobArchitectureId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<LevelingFrameworkGenerationResponse, HttpFetcherError>>;
    readonly searchJobArchitecture: (args: {
        readonly acceptLanguage?: LanguageCode;
        readonly detail?: boolean;
        readonly jobArchitectureId: string;
        readonly page?: number;
        readonly reviewerAssignment?: 'unassigned';
        readonly reviewerId?: string;
        readonly reviewState?: 'approved' | 'assigned';
        readonly searchTerm?: string;
        readonly size?: number;
        readonly state?: 'active' | 'archived' | 'draft';
        readonly sort?: {
            readonly direction: 'asc' | 'desc';
            readonly field: string;
        };
        readonly type?:
            | 'cluster'
            | 'family'
            | 'level'
            | 'role'
            | ReadonlyArray<'cluster' | 'family' | 'level' | 'role'>;
    }) => Promise<Result<JobArchitectureSearchResponseWithDetail, HttpFetcherError>>;
    readonly searchJobArchitectures: (args: {
        readonly detail?: boolean;
        readonly page?: number;
        readonly reviewerAssignment?: 'unassigned';
        readonly reviewerId?: string;
        readonly reviewState?: 'approved' | 'assigned';
        readonly searchTerm?: string;
        readonly size?: number;
        readonly state?: 'active' | 'archived' | 'draft';
        readonly sort?: {
            readonly direction: 'asc' | 'desc';
            readonly field: string;
        };
        readonly type?:
            | 'cluster'
            | 'family'
            | 'level'
            | 'role'
            | ReadonlyArray<'cluster' | 'family' | 'level' | 'role'>;
    }) => Promise<Result<JobArchitectureSearchResponseWithDetail, HttpFetcherError>>;
    readonly searchTaskManagementTasks: (args: {
        readonly params: TaskManagementSearchRequestInput;
    }) => Promise<Result<TaskManagementTasksResponse, HttpFetcherError>>;
    readonly unarchiveJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unarchiveJobLevels: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unarchiveJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unarchiveJobRoles: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unassignReviewer: (args: {
        readonly item: {
            readonly id: string;
            readonly architectureId: string;
            readonly type: 'job-level' | 'job-role';
        };
    }) => Promise<Result<JobLevel | JobRole, HttpFetcherError>>;
    readonly unreviewJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unreviewJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly updateJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
        readonly payload: UpdateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly updateJobItemProfile: (args: {
        readonly acceptLanguage: string;
        readonly architectureId: string;
        readonly jobItemType:
            | { readonly id: string; readonly type: 'job-level' }
            | { readonly id: string; readonly type: 'job-role' };
        readonly jobProfile: JobProfileLegacy;
        readonly tasks?: JobProfileUpdatePayload['tasks'];
        readonly tasksBaseVersion?: number;
        readonly versionComment?: string;
        readonly withAutoTranslation: boolean;
    }) => Promise<Result<JobLevel | JobRole, HttpFetcherError>>;
    readonly updateJobProfile: (args: {
        readonly acceptLanguage: string;
        readonly jobProfile: JobProfileLegacy;
        readonly versionComment?: string;
        readonly withAutoTranslation: boolean;
    }) => Promise<Result<JobProfileLegacy, HttpFetcherError>>;
    readonly updateLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly payload: UpdateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly updateSkillCatalogEntry: (args: {
        readonly skillId: string;
        readonly syncStatus: SkillSyncStatus;
    }) => Promise<Result<unknown, HttpFetcherError>>;

    // Job Profile Template fetchers
    readonly fetchManagedJobProfileTemplates: () => Promise<
        Result<readonly ManagedJobProfileTemplateResponse[], HttpFetcherError>
    >;
    readonly fetchCustomJobProfileTemplate: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<CustomJobProfileTemplateResponse | null, HttpFetcherError>>;
    readonly putCustomJobProfileTemplate: (args: {
        readonly jobArchitectureId: string;
        readonly templateId: string;
        readonly payload: CustomJobProfileTemplateRequest;
    }) => Promise<Result<CustomJobProfileTemplateResponse, HttpFetcherError>>;
    readonly deleteCustomJobProfileTemplate: (args: {
        readonly jobArchitectureId: string;
        readonly templateId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly postJobProfileTemplatePreview: (args: {
        readonly payload: CustomJobProfileTemplateRequest;
    }) => Promise<Result<unknown, HttpFetcherError>>;
};

export type JobArchitectureFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: JsonFetcher;
};

const buildAutoTranslationSearchParams = (
    withAutoTranslation: boolean,
): { readonly asyncTranslation: true; readonly shouldTranslate: true } | { readonly shouldTranslate: false } =>
    withAutoTranslation ? { asyncTranslation: true, shouldTranslate: true } : { shouldTranslate: false };

const createAcceptLanguageHeaders = (
    acceptLanguage: LanguageCode | undefined,
): { readonly 'Accept-Language': LanguageCode } | undefined =>
    acceptLanguage === undefined ? undefined : { 'Accept-Language': acceptLanguage };

export const createJobArchitectureFetchers = (
    dependencies: JobArchitectureFetchersDependencies,
): JobArchitectureFetchers => {
    const { cobrainerApiFetcher, translate, validatingCobrainerApiFetcher } = dependencies;

    const generateWithAiRequestTimeout = 60_000;

    const careerTrackWithTradeUnionsSchema = createCareerTrackWithTradeUnionsSchema(translate);
    const addCareerTrack: JobArchitectureFetchers['addCareerTrack'] = async ({ careerTrack, jobArchitectureId }) => {
        return validatingCobrainerApiFetcher.postJson(careerTrackWithTradeUnionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/career-tracks`,
            payload: careerTrack,
        });
    };

    const assignReviewer: JobArchitectureFetchers['assignReviewer'] = async ({ item }) => {
        const payload = {
            reviewer: item.reviewer,
        };

        if ('type' in item && item.type === 'job-role') {
            return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
                pathname: `/job-architectures/${item.architectureId}/roles/${item.id}`,
                payload,
            });
        }

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${item.architectureId}/levels/${item.id}`,
            payload,
        });
    };

    const unassignReviewer: JobArchitectureFetchers['unassignReviewer'] = async ({ item }) => {
        const payload = {
            reviewer: null,
        };

        if (item.type === 'job-role') {
            return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
                pathname: `/job-architectures/${item.architectureId}/roles/${item.id}`,
                payload,
            });
        }

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${item.architectureId}/levels/${item.id}`,
            payload,
        });
    };

    const copyJobClusters: JobArchitectureFetchers['copyJobClusters'] = async ({ jobClusters, jobFamilyId }) => {
        try {
            const results = await Promise.all(
                jobClusters.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/clusters/${id}/copy-to/${jobFamilyId}`,
                        requestOptions: {
                            searchParams: {
                                recursive: false,
                            },
                        },
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job clusters'));
        }
    };

    const copyJobLevels: JobArchitectureFetchers['copyJobLevels'] = async ({ jobLevels, jobRoleId }) => {
        try {
            const results = await Promise.all(
                jobLevels.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/levels/${id}/copy-to/${jobRoleId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job levels'));
        }
    };

    const copyJobRoles: JobArchitectureFetchers['copyJobRoles'] = async ({ jobClusterId, jobRoles }) => {
        try {
            const results = await Promise.all(
                jobRoles.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/roles/${id}/copy-to/${jobClusterId}`,
                        requestOptions: {
                            searchParams: {
                                recursive: false,
                            },
                        },
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job roles'));
        }
    };

    const fetchAssignableReviewers: JobArchitectureFetchers['fetchAssignableReviewers'] = async ({
        abortSignal,
        jobRoleOrJobLevel,
        searchTerm = '',
    }) => {
        const pathname: Pathname =
            jobRoleOrJobLevel.type === 'role'
                ? `/job-architectures/${jobRoleOrJobLevel.architectureId}/roles/${jobRoleOrJobLevel.id}/reviewers`
                : `/job-architectures/${jobRoleOrJobLevel.architectureId}/levels/${jobRoleOrJobLevel.id}/reviewers`;

        return validatingCobrainerApiFetcher.get(personsSchema, {
            pathname,
            requestOptions: {
                searchParams: {
                    searchTerm,
                },
                signal: abortSignal,
            },
        });
    };

    const fetchCareerTracks: JobArchitectureFetchers['fetchCareerTracks'] = async (jobArchitectureId) => {
        return validatingCobrainerApiFetcher.get(careerTrackWithTradeUnionsSchema.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/career-tracks`,
        });
    };

    const fetchCompetencies: JobArchitectureFetchers['fetchCompetencies'] = async () => {
        return validatingCobrainerApiFetcher.get(competenciesSchema, {
            pathname: '/skills/competencies',
        });
    };

    const fetchCommentStatistics: JobArchitectureFetchers['fetchCommentStatistics'] = async ({
        from,
        jobArchitectureId,
        language,
        to,
    }) => {
        return validatingCobrainerApiFetcher.get(commentStatisticsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/comments/statistics`,
            requestOptions: {
                searchParams: {
                    language,
                    ...(is.undefined(from) ? {} : { from }),
                    ...(is.undefined(to) ? {} : { to }),
                },
            },
        });
    };

    const fetchContributionTimeline: JobArchitectureFetchers['fetchContributionTimeline'] = async ({
        days = 30,
        jobArchitectureId,
        timezone,
    }) => {
        return validatingCobrainerApiFetcher.get(contributionTimelineSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/contributions/timeline`,
            requestOptions: {
                searchParams: {
                    days,
                    timezone,
                },
            },
        });
    };

    const fetchSkillDetails: JobArchitectureFetchers['fetchSkillDetails'] = async ({ language, skillId }) => {
        return validatingCobrainerApiFetcher.get(skillDetailsSchema, {
            pathname: `/skills/${skillId}/details`,
            requestOptions: {
                headers: {
                    'Accept-Language': language,
                },
            },
        });
    };

    const fetchSkillReferences: JobArchitectureFetchers['fetchSkillReferences'] = async ({ language, skillId }) => {
        return validatingCobrainerApiFetcher.get(skillReferencesSchema, {
            pathname: `/skills/${skillId}/references`,
            requestOptions: {
                headers: {
                    'Accept-Language': language,
                },
            },
        });
    };

    const fetchSkillLimits: JobArchitectureFetchers['fetchSkillLimits'] = async ({ jobArchitectureId }) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureSkillLimitsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/skill-limits`,
        });
    };

    const fetchSkillLocales: JobArchitectureFetchers['fetchSkillLocales'] = async () => {
        return validatingCobrainerApiFetcher.get(z.string().array(), {
            pathname: '/skills/locales',
        });
    };

    const fetchJobLevelVersions: JobArchitectureFetchers['fetchJobLevelVersions'] = async ({
        jobArchitectureId,
        jobLevelId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobProfileVersionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions`,
        });
    };

    const fetchJobRoleVersions: JobArchitectureFetchers['fetchJobRoleVersions'] = async ({
        jobArchitectureId,
        jobRoleId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobProfileVersionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions`,
        });
    };

    const fetchSkillsBySearchTerm: JobArchitectureFetchers['fetchSkillsBySearchTerm'] = async ({
        acceptLanguage,
        searchTerm,
    }) => {
        return validatingCobrainerApiFetcher.get(skillShortSchema.array(), {
            pathname: `/skills`,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams: {
                    limit: 10,
                    searchTerm,
                    type: ['hard', 'soft', 'language'].join(','),
                },
            },
        });
    };

    const fetchJobArchitectureSkills: JobArchitectureFetchers['fetchJobArchitectureSkills'] = async ({
        acceptLanguage,
        params,
    }) => {
        const { jobArchitectureIds, levelIds, paged, roleIds, searchTerm, skillTypes, sort, sources, statusFilter } =
            params;

        return validatingCobrainerApiFetcher.get(paginatedSkillCatalogSchema, {
            pathname: `/skill-catalog`,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams: {
                    countOnly: paged.countOnly,
                    jobArchitectureIds: jobArchitectureIds?.join(','),
                    levelIds: levelIds?.join(','),
                    page: paged.page,
                    roleIds: roleIds?.join(','),
                    searchTerm,
                    size: paged.size,
                    skillTypes: skillTypes?.join(','),
                    sort,
                    sources: sources?.join(','),
                    statusFilter: statusFilter?.join(','),
                },
                timeout: 50_000,
            },
        });
    };

    const fetchSkillCatalogOptions: JobArchitectureFetchers['fetchSkillCatalogOptions'] = async () => {
        return validatingCobrainerApiFetcher.get(skillCatalogOptionsSchema, {
            pathname: `/skill-catalog/options`,
        });
    };

    const fetchTaskManagementClusters: JobArchitectureFetchers['fetchTaskManagementClusters'] = async ({
        architectureId,
        searchTerm,
    }) =>
        validatingCobrainerApiFetcher.get(taskManagementClustersResponseSchema, {
            pathname: `/job-architectures/${architectureId}/task-clusters/tasks`,
            requestOptions: { searchParams: { searchTerm } },
        });

    const searchTaskManagementClusterTasks: JobArchitectureFetchers['searchTaskManagementClusterTasks'] = async ({
        architectureId,
        params,
    }) =>
        validatingCobrainerApiFetcher.postJson(taskManagementTasksResponseSchema, {
            pathname: `/job-architectures/${architectureId}/task-clusters/tasks/search`,
            payload: params,
        });

    const fetchTaskManagementTask: JobArchitectureFetchers['fetchTaskManagementTask'] = async ({
        acceptLanguage,
        taskId,
        version,
    }) => {
        return validatingCobrainerApiFetcher.get(taskManagementTaskDetailSchema, {
            pathname: is.undefined(version) ? `/work-tasks/${taskId}` : `/work-tasks/${taskId}/versions/${version}`,
            requestOptions: {
                headers: is.undefined(acceptLanguage) ? undefined : { 'Accept-Language': acceptLanguage },
            },
        });
    };

    const fetchWorkTaskAutomationRecommendation: JobArchitectureFetchers['fetchWorkTaskAutomationRecommendation'] =
        async ({ taskId }) =>
            validatingCobrainerApiFetcher.get(workTaskAutomationRecommendationSchema, {
                pathname: `/work-tasks/${taskId}/automation-recommendation`,
            });

    const searchTaskManagementTasks: JobArchitectureFetchers['searchTaskManagementTasks'] = async ({ params }) => {
        return validatingCobrainerApiFetcher.postJson(taskManagementTasksResponseSchema, {
            pathname: `/work-tasks/search`,
            payload: params,
        });
    };

    const fetchSkillCatalogStatistics: JobArchitectureFetchers['fetchSkillCatalogStatistics'] = async ({
        jobArchitectureIds,
        levelIds,
        roleIds,
        searchTerm,
        skillTypes,
        sources,
        statusFilter,
    }) => {
        return validatingCobrainerApiFetcher.get(skillCatalogStatisticsSchema, {
            pathname: `/skill-catalog/statistics`,
            requestOptions: {
                searchParams: {
                    jobArchitectureIds: jobArchitectureIds?.join(','),
                    levelIds: levelIds?.join(','),
                    roleIds: roleIds?.join(','),
                    searchTerm,
                    skillTypes: skillTypes?.join(','),
                    sources: sources?.join(','),
                    statusFilter: statusFilter?.join(','),
                },
            },
        });
    };

    const createSkillCatalogEntry: JobArchitectureFetchers['createSkillCatalogEntry'] = async ({
        skillId,
        syncStatus,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/skill-catalog`,
            payload: {
                skillId,
                syncStatus,
            },
        });
    };

    const requestSkillCatalogExport: JobArchitectureFetchers['requestSkillCatalogExport'] = async ({
        onlyExternallySynced,
    }) => {
        return validatingCobrainerApiFetcher.postJson(queuedExportResponseSchema, {
            pathname: '/skill-catalog/export',
            payload: { onlyExternallySynced },
        });
    };

    const deleteSkillCatalogEntry: JobArchitectureFetchers['deleteSkillCatalogEntry'] = async ({ skillId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/skill-catalog/${skillId}`,
        });
    };

    const updateSkillCatalogEntry: JobArchitectureFetchers['updateSkillCatalogEntry'] = async ({
        skillId,
        syncStatus,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/skill-catalog/${skillId}`,
            payload: {
                syncStatus,
            },
        });
    };

    // --- Job Profile Template fetchers ---

    const fetchManagedJobProfileTemplates: JobArchitectureFetchers['fetchManagedJobProfileTemplates'] = async () => {
        return validatingCobrainerApiFetcher.get(managedJobProfileTemplateResponseArraySchema, {
            pathname: '/job-architecture/managed-job-profile-templates',
        });
    };

    const fetchCustomJobProfileTemplate: JobArchitectureFetchers['fetchCustomJobProfileTemplate'] = async ({
        jobArchitectureId,
    }) => {
        const result = await validatingCobrainerApiFetcher.get(customJobProfileTemplateGetResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/job-profile-templates`,
        });

        return result.map((response) => response.template);
    };

    const putCustomJobProfileTemplate: JobArchitectureFetchers['putCustomJobProfileTemplate'] = async ({
        jobArchitectureId,
        templateId,
        payload,
    }) => {
        return validatingCobrainerApiFetcher.putJson(customJobProfileTemplateResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/job-profile-templates/${templateId}`,
            payload,
            requestOptions: {
                timeout: 30_000,
            },
        });
    };

    const deleteCustomJobProfileTemplate: JobArchitectureFetchers['deleteCustomJobProfileTemplate'] = async ({
        jobArchitectureId,
        templateId,
    }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architecture/${jobArchitectureId}/job-profile-templates/${templateId}`,
        });
    };

    const postJobProfileTemplatePreview: JobArchitectureFetchers['postJobProfileTemplatePreview'] = async ({
        payload,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: '/job-architecture/job-profile-templates/preview',
            payload,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const fetchRelatedSkills: JobArchitectureFetchers['fetchRelatedSkills'] = async ({ acceptLanguage, skills }) => {
        return validatingCobrainerApiFetcher.postJson(skillShortSchema.array(), {
            pathname: `/skills/related`,
            payload: {
                hardSkillsLimit: 10,
                languageSkillsLimit: 1,
                skills,
                softSkillsLimit: 10,
            },
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
            },
        });
    };

    const fetchDebugInformation: JobArchitectureFetchers['fetchDebugInformation'] = async (item) => {
        const path = (item: JobCluster | JobFamily | JobLevel | JobRole): Pathname => {
            switch (item.type) {
                case 'job-family':
                    return `/job-architectures/${item.architectureId}/families/${item.id}/generationInfo`;
                case 'job-cluster':
                    return `/job-architectures/${item.architectureId}/clusters/${item.id}/generationInfo`;
                case 'job-role':
                    return `/job-architectures/${item.architectureId}/roles/${item.id}/generationInfo`;
                case 'job-level':
                    return `/job-architectures/${item.architectureId}/levels/${item.id}/generationInfo`;
            }
        };

        return validatingCobrainerApiFetcher.get(jobArchitectureDebugInfoSchema, {
            pathname: path(item),
        });
    };

    const fetchJobArchitecture: JobArchitectureFetchers['fetchJobArchitecture'] = async (
        jobArchitectureId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureSchema, {
            pathname: `/job-architectures/${jobArchitectureId}`,
            requestOptions: { signal },
        });
    };

    const createJobArchitecture: JobArchitectureFetchers['createJobArchitecture'] = async () => {
        return validatingCobrainerApiFetcher.postJson(jobArchitectureSchema, {
            pathname: '/job-architectures',
            payload: {
                translations: [],
            },
        });
    };

    const fetchJobArchitectures: JobArchitectureFetchers['fetchJobArchitectures'] = async () => {
        return validatingCobrainerApiFetcher.get(jobArchitecturesSchema, {
            pathname: '/job-architectures',
        });
    };

    const fetchJobArchitectureOptions: JobArchitectureFetchers['fetchJobArchitectureOptions'] = async () => {
        return validatingCobrainerApiFetcher.get(jobArchitectureOptionsResponseSchema, {
            pathname: '/job-architectures/options',
        });
    };

    const fetchJobArchitectureMetadata: JobArchitectureFetchers['fetchJobArchitectureMetadata'] = async (
        jobArchitectureId,
    ) => {
        return validatingCobrainerApiFetcher.get(nullableJobArchitectureMetadataSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/metadata`,
        });
    };

    const fetchCompanyInformationGenerationStatus: JobArchitectureFetchers['fetchCompanyInformationGenerationStatus'] =
        ({ jobArchitectureId, signal }) => {
            return validatingCobrainerApiFetcher.get(companyInformationGenerationResponseSchema, {
                pathname: `/job-architectures/${jobArchitectureId}/company-information/generate`,
                requestOptions: { signal },
            });
        };

    const submitCompanyInformationGeneration: JobArchitectureFetchers['submitCompanyInformationGeneration'] = async ({
        jobArchitectureId,
        payload,
        regenerate,
        signal,
    }) => {
        return validatingCobrainerApiFetcher.postJson(companyInformationGenerationResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/company-information/generate`,
            payload,
            requestOptions: {
                searchParams: { regenerate: String(regenerate) },
                signal,
            },
        });
    };

    const submitLevelingFrameworkAssessment: JobArchitectureFetchers['submitLevelingFrameworkAssessment'] = async ({
        documentIds,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.postJson(levelingFrameworkAssessmentResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/assessments`,
            payload: { documentIds },
        });
    };

    const submitLevelingFrameworkGeneration: JobArchitectureFetchers['submitLevelingFrameworkGeneration'] = async ({
        jobArchitectureId,
        payload,
    }) => {
        return validatingCobrainerApiFetcher.postJson(levelingFrameworkGenerationResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/generations`,
            payload,
        });
    };

    const fetchLevelingFrameworkGeneration: JobArchitectureFetchers['fetchLevelingFrameworkGeneration'] = ({
        generationId,
        jobArchitectureId,
        signal,
    }) => {
        return validatingCobrainerApiFetcher.get(levelingFrameworkGenerationResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/generations/${generationId}`,
            requestOptions: { signal },
        });
    };

    const fetchLevelingFrameworkAssessment: JobArchitectureFetchers['fetchLevelingFrameworkAssessment'] = ({
        assessmentId,
        jobArchitectureId,
        signal,
    }) => {
        return validatingCobrainerApiFetcher.get(levelingFrameworkAssessmentResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/assessments/${assessmentId}`,
            requestOptions: { signal },
        });
    };

    const fetchJobArchitectureOnboardingState: JobArchitectureFetchers['fetchJobArchitectureOnboardingState'] = async (
        jobArchitectureId,
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureOnboardingStateSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/onboarding-state`,
        });
    };

    const patchJobArchitectureOnboardingState: JobArchitectureFetchers['patchJobArchitectureOnboardingState'] = async ({
        jobArchitectureId,
        onboardingState: updates,
    }) => {
        return validatingCobrainerApiFetcher.patchJson(jobArchitectureOnboardingStateSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/onboarding-state`,
            payload: updates,
        });
    };

    const fetchJobArchitectureInflight: JobArchitectureFetchers['fetchJobArchitectureInflight'] = async (
        jobArchitectureId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureInflightSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/inflight-tasks`,
            requestOptions: { signal },
        });
    };

    const fetchFamiliesWithClusters: JobArchitectureFetchers['fetchFamiliesWithClusters'] = async (
        jobArchitectureId,
        { acceptLanguage, signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(familyWithClustersSchema.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families-with-clusters`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                signal,
            },
        });
    };

    const fetchJobClusters: JobArchitectureFetchers['fetchJobClusters'] = async (
        jobArchitectureId,
        jobFamilyId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobClusterWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters`,
            requestOptions: { signal },
        });
    };

    const fetchJobFamilies: JobArchitectureFetchers['fetchJobFamilies'] = async (
        jobArchitectureId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobFamilyWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families`,
            requestOptions: { signal },
        });
    };

    const fetchJobRoles: JobArchitectureFetchers['fetchJobRoles'] = async (
        jobArchitectureId,
        jobClusterId,
        { acceptLanguage, signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${jobClusterId}/roles`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                searchParams: {
                    includeProfiles: false,
                },
                signal,
            },
        });
    };

    const fetchJobLevels: JobArchitectureFetchers['fetchJobLevels'] = async (
        jobArchitectureId,
        jobRoleId,
        { acceptLanguage, signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/levels`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                searchParams: {
                    includeProfiles: false,
                },
                signal,
            },
        });
    };

    const deleteJobClusters: JobArchitectureFetchers['deleteJobClusters'] = async (jobClusters) => {
        const { architectureId } = jobClusters[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/clusters`,
            payload: jobClusters.map(({ id }) => id),
        });
    };

    const deleteJobFamilies: JobArchitectureFetchers['deleteJobFamilies'] = async (jobFamilies) => {
        const { architectureId } = jobFamilies[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/families`,
            payload: jobFamilies.map(({ id }) => id),
        });
    };

    const deleteJobLevels: JobArchitectureFetchers['deleteJobLevels'] = async (jobLevels) => {
        const { architectureId } = jobLevels[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/levels`,
            payload: jobLevels.map(({ id }) => id),
        });
    };

    const deleteJobRoles: JobArchitectureFetchers['deleteJobRoles'] = async (jobRoles) => {
        const { architectureId } = jobRoles[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/roles`,
            payload: jobRoles.map(({ id }) => id),
        });
    };

    const exportJobArchitecture: JobArchitectureFetchers['exportJobArchitecture'] = async ({
        jobArchitectureId,
        languageCode,
    }) => {
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/exports`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(queuedExportResponseSchema, {
            pathname,
            requestOptions: {
                headers,
            },
        });
    };

    const requestChangelogExport: JobArchitectureFetchers['requestChangelogExport'] = async ({
        jobArchitectureId,
        dateFrom,
        dateTo,
        familyOrClusterId,
        recipientEmail,
    }) => {
        return validatingCobrainerApiFetcher.postJson(queuedExportResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/changelog/export`,
            payload: {
                ...(dateFrom !== undefined && { dateFrom }),
                ...(dateTo !== undefined && { dateTo }),
                ...(familyOrClusterId !== undefined && { familyOrClusterId }),
                ...(recipientEmail !== undefined && recipientEmail !== '' && { recipientEmail }),
            },
        });
    };

    const exportJobLevel: JobArchitectureFetchers['exportJobLevel'] = async ({
        jobArchitectureId,
        jobLevelId,
        languageCode,
        version,
    }) => {
        const versionQuery = is.number(version) ? `?version=${version}` : '';
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/export${versionQuery}`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
            },
        });
    };

    const exportJobRole: JobArchitectureFetchers['exportJobRole'] = async ({
        jobArchitectureId,
        jobRoleId,
        languageCode,
        version,
    }) => {
        const versionQuery = is.number(version) ? `?version=${version}` : '';
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/export${versionQuery}`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
            },
        });
    };

    const patchJobArchitecture: JobArchitectureFetchers['patchJobArchitecture'] = async ({
        jobArchitecture,
        languageCode,
    }) => {
        const { id, title } = jobArchitecture;

        return validatingCobrainerApiFetcher.patchJson(jobArchitectureSchema, {
            pathname: `/job-architectures/${id}`,
            payload: {
                translations: [
                    {
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const patchJobArchitectureMetadata: JobArchitectureFetchers['patchJobArchitectureMetadata'] = async ({
        jobArchitectureId,
        metadata,
        allowJsaRename,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/job-architectures/${jobArchitectureId}/metadata`,
            payload: metadata,
            requestOptions: allowJsaRename === true ? { searchParams: { allowJsaRename: 'true' } } : undefined,
        });
    };

    const patchJobFamily: JobArchitectureFetchers['patchJobFamily'] = async ({ jobFamily, withAutoTranslation }) => {
        const { id, architectureId } = jobFamily;

        const searchParams = buildAutoTranslationSearchParams(withAutoTranslation);

        return validatingCobrainerApiFetcher.patchJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/families/${id}`,
            payload: {
                translations: jobFamily.translations,
            },
            requestOptions: { searchParams },
        });
    };

    const patchJobCluster: JobArchitectureFetchers['patchJobCluster'] = async ({ jobCluster, withAutoTranslation }) => {
        const { id, architectureId, translations } = jobCluster;

        return validatingCobrainerApiFetcher.patchJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/clusters/${id}`,
            payload: {
                translations,
            },
            requestOptions: { searchParams: buildAutoTranslationSearchParams(withAutoTranslation) },
        });
    };

    const patchJobLevel: JobArchitectureFetchers['patchJobLevel'] = async ({ jobLevel, languageCode }) => {
        const { id, architectureId, description, title } = jobLevel;

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/levels/${id}`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const patchJobRole: JobArchitectureFetchers['patchJobRole'] = async ({ jobRole, languageCode }) => {
        const { id, architectureId, description, title } = jobRole;

        return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/roles/${id}`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const putJobCluster: JobArchitectureFetchers['putJobCluster'] = async ({
        jobArchitectureId,
        jobCluster,
        jobFamilyId,
    }) => {
        return validatingCobrainerApiFetcher.putJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters/${jobCluster.id}`,
            payload: { translations: jobCluster.translations },
        });
    };

    const putJobFamily: JobArchitectureFetchers['putJobFamily'] = async ({ jobArchitectureId, jobFamily }) => {
        return validatingCobrainerApiFetcher.putJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamily.id}`,
            payload: { translations: jobFamily.translations },
        });
    };

    const bulkApplyOnboardingDraft: JobArchitectureFetchers['bulkApplyOnboardingDraft'] = async ({
        draft,
        jobArchitectureId,
        languageCode,
    }) => {
        try {
            await applyOnboardingDraft(
                { draft, jobArchitectureId, languageCode },
                {
                    deleteJobClusters,
                    deleteJobFamilies,
                    patchJobCluster,
                    patchJobFamily,
                    putJobCluster,
                    putJobFamily,
                },
            );

            return Result.ok(undefined);
        } catch (error) {
            return Result.err(error instanceof Error ? error : new Error(String(error)));
        }
    };

    const persistJobCluster: JobArchitectureFetchers['persistJobCluster'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families/${parentId}/clusters?generateDescription=true`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobFamily: JobArchitectureFetchers['persistJobFamily'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families?generateDescription=true`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobLevel: JobArchitectureFetchers['persistJobLevel'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${parentId}/levels`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobRole: JobArchitectureFetchers['persistJobRole'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${parentId}/roles`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const editJobLevelVersionNote: JobArchitectureFetchers['editJobLevelVersionNote'] = async ({
        comment,
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}`,
            payload: { comment },
        });
    };

    const editJobRoleVersionNote: JobArchitectureFetchers['editJobRoleVersionNote'] = async ({
        comment,
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}`,
            payload: { comment },
        });
    };

    const publishJobLevelVersion: JobArchitectureFetchers['publishJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/publish`,
        });
    };

    const publishJobRoleVersion: JobArchitectureFetchers['publishJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/publish`,
        });
    };

    const unpublishJobLevelVersion: JobArchitectureFetchers['unpublishJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/unpublish`,
        });
    };

    const unpublishJobRoleVersion: JobArchitectureFetchers['unpublishJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/unpublish`,
        });
    };

    const reviewJobLevelVersion: JobArchitectureFetchers['reviewJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/review`,
        });
    };

    const reviewJobRoleVersion: JobArchitectureFetchers['reviewJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/review`,
        });
    };

    const unreviewJobLevelVersion: JobArchitectureFetchers['unreviewJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/unreview`,
        });
    };

    const unreviewJobRoleVersion: JobArchitectureFetchers['unreviewJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/unreview`,
        });
    };

    const readJobLevel: JobArchitectureFetchers['readJobLevel'] = async ({ jobArchitectureId, jobLevelId, signal }) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}`,
            requestOptions: { signal },
        });
    };

    const readJobRole: JobArchitectureFetchers['readJobRole'] = async ({ jobArchitectureId, jobRoleId, signal }) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}`,
            requestOptions: { signal },
        });
    };

    const readJobLevelDetails: JobArchitectureFetchers['readJobLevelDetails'] = async ({
        includeComments,
        jobArchitectureId,
        jobLevelId,
        signal,
        version,
    }) => {
        const queryParams = new URLSearchParams();
        if (!is.undefined(version)) {
            queryParams.set('version', version);
        }
        if (includeComments === true) {
            queryParams.set('includeComments', 'true');
        }

        return validatingCobrainerApiFetcher.get(jobLevelDetailsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}?${queryParams.toString()}`,
            requestOptions: { signal },
        });
    };

    const readJobRoleDetails: JobArchitectureFetchers['readJobRoleDetails'] = async ({
        includeComments,
        jobArchitectureId,
        jobRoleId,
        signal,
        version,
    }) => {
        const queryParams = new URLSearchParams();
        if (!is.undefined(version)) {
            queryParams.set('version', version);
        }
        if (includeComments === true) {
            queryParams.set('includeComments', 'true');
        }

        return await validatingCobrainerApiFetcher.get(jobRoleDetailsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}?${queryParams.toString()}`,
            requestOptions: { signal },
        });
    };

    const redetectSkills: JobArchitectureFetchers['redetectSkills'] = async ({
        additionalPrompt,
        commentsContext,
        detectionType,
        jobProfile,
        jobProfileId,
    }) => {
        const requestPayload = {
            additionalPrompt: {
                type: detectionType,
                userInput: additionalPrompt.unwrapOr(undefined),
            },
            ...(commentsContext ? { commentsContext } : {}),
            jobProfile,
        };

        return validatingCobrainerApiFetcher.postJson(redetectSkillsWithPromptSchema, {
            pathname: `/job-profiles/${jobProfileId}/detect-skills`,
            payload: requestPayload,
            requestOptions: {
                timeout: 30_000,
            },
        });
    };

    const updateJobItemProfile: JobArchitectureFetchers['updateJobItemProfile'] = async ({
        acceptLanguage,
        architectureId,
        jobItemType,
        jobProfile,
        tasks,
        tasksBaseVersion,
        versionComment,
        withAutoTranslation,
    }) => {
        const searchParams = buildAutoTranslationSearchParams(withAutoTranslation);
        const payload = createJobProfileUpdatePayload(jobProfile, versionComment, tasks, tasksBaseVersion);
        const requestOptions = {
            headers: {
                'Accept-Language': acceptLanguage,
            },
            searchParams,
        };

        if (jobItemType.type === 'job-role') {
            return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
                pathname: `/job-architectures/${architectureId}/roles/${jobItemType.id}`,
                payload,
                requestOptions,
            });
        }

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/levels/${jobItemType.id}`,
            payload,
            requestOptions,
        });
    };

    const updateJobProfile: JobArchitectureFetchers['updateJobProfile'] = async ({
        acceptLanguage,
        jobProfile,
        versionComment,
        withAutoTranslation,
    }) => {
        const searchParams = buildAutoTranslationSearchParams(withAutoTranslation);
        const payload = createJobProfileLegacyUpdatePayload(jobProfile, versionComment);

        return validatingCobrainerApiFetcher.patchJson(jobProfileLegacySchema, {
            pathname: `/job-profiles/${jobProfile.id}`,
            payload,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams,
            },
        });
    };

    const cancelJobArchitectureGenerations: JobArchitectureFetchers['cancelJobArchitectureGenerations'] = () =>
        cobrainerApiFetcher.post({ pathname: '/job-architectures/cancel-generations' });

    const generateJobArchitectureWithAI: JobArchitectureFetchers['generateJobArchitectureWithAI'] = async ({
        acceptLanguage,
        awaitKnowledgeBaseIngestion,
        jobArchitectureId,
        type,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/generate`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                searchParams: {
                    async: true,
                    awaitKnowledgeBaseIngestion,
                    type,
                },
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobFamilyWithAI: JobArchitectureFetchers['generateJobFamilyWithAI'] = async ({
        acceptLanguage,
        jobArchitectureId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/families/generate?async=true`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobClustersWithAI: JobArchitectureFetchers['generateJobClustersWithAI'] = async ({
        acceptLanguage,
        jobArchitectureId,
        jobFamilyId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters/generate?async=true`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const getJobArchitectureItemPath: JobArchitectureFetchers['getJobArchitectureItemPath'] = async ({
        itemId,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobArchitecturePathSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/items/${itemId}/path`,
        });
    };

    const getUsers: JobArchitectureFetchers['getUsers'] = async ({ paged, role, sort }) => {
        return validatingCobrainerApiFetcher.get(userPaginationSchema, {
            pathname: '/users',
            requestOptions: {
                searchParams: {
                    page: paged.page,
                    size: paged.size,
                    ...(paged.countOnly === true ? { countOnly: paged.countOnly } : {}),
                    ...(!is.nullOrUndefined(role) ? { role } : {}),
                    ...(!is.nullOrUndefined(sort) ? { sort } : {}),
                },
            },
        });
    };

    const generateJobRolesWithAI: JobArchitectureFetchers['generateJobRolesWithAI'] = async ({
        acceptLanguage,
        jobArchitectureId,
        jobClusterId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${jobClusterId}/roles/generate?async=true`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobLevelsWithAI: JobArchitectureFetchers['generateJobLevelsWithAI'] = async ({
        acceptLanguage,
        autoSelectTracks,
        careerTracks,
        createRoleCopy,
        generateLevels,
        jobArchitectureId,
        jobRoleId,
        respectSkillsFromRole,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/levels/generate?async=true`,
            payload: {
                autoSelectTracks,
                careerTracks,
                createRoleCopy,
                generateLevels,
                respectSkillsFromRole,
            },
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobLevelsForAllRoles: JobArchitectureFetchers['generateJobLevelsForAllRoles'] = async ({
        acceptLanguage,
        createRoleCopy,
        generateLevels,
        jobArchitectureId,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/generate`,
            payload: { createRoleCopy, generateLevels },
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                searchParams: { async: true, type: 'levels-for-all-roles' },
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const moveJobClusters: JobArchitectureFetchers['moveJobClusters'] = async ({ jobClusters, jobFamilyId }) => {
        try {
            const results = await Promise.all(
                jobClusters.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/clusters/${id}/move-to/${jobFamilyId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job clusters'));
        }
    };

    const moveJobLevels: JobArchitectureFetchers['moveJobLevels'] = async ({
        jobArchitectureId,
        jobLevels,
        jobRoleId,
    }) => {
        try {
            const results = await Promise.all(
                jobLevels.map(({ id }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${jobArchitectureId}/levels/${id}/move-to/${jobRoleId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job levels'));
        }
    };

    const moveJobRoles: JobArchitectureFetchers['moveJobRoles'] = async ({
        jobArchitectureId,
        jobClusterId,
        jobRoles,
    }) => {
        try {
            const results = await Promise.all(
                jobRoles.map(({ id }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${jobArchitectureId}/roles/${id}/move-to/${jobClusterId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job roles'));
        }
    };

    const reorderJobFamilies: JobArchitectureFetchers['reorderJobFamilies'] = ({ itemIds, jobArchitectureId }) =>
        cobrainerApiFetcher.putJson({
            pathname: `/job-architectures/${jobArchitectureId}/families/order`,
            payload: { itemIds },
        });

    const reorderJobClusters: JobArchitectureFetchers['reorderJobClusters'] = ({
        itemIds,
        jobArchitectureId,
        jobFamilyId,
    }) =>
        cobrainerApiFetcher.putJson({
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters/order`,
            payload: { itemIds },
        });

    const reorderJobRoles: JobArchitectureFetchers['reorderJobRoles'] = ({
        itemIds,
        jobArchitectureId,
        jobClusterId,
    }) =>
        cobrainerApiFetcher.putJson({
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${jobClusterId}/roles/order`,
            payload: { itemIds },
        });

    const reorderJobLevels: JobArchitectureFetchers['reorderJobLevels'] = ({ itemIds, jobArchitectureId, jobRoleId }) =>
        cobrainerApiFetcher.putJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/levels/order`,
            payload: { itemIds },
        });

    const regenerateJobProfileDescription: JobArchitectureFetchers['regenerateJobProfileDescription'] = ({
        acceptLanguage,
        additionalPrompt,
        commentsContext,
        jobProfile,
    }) => {
        const requestPayload = {
            additionalPrompt: additionalPrompt.unwrapOr(undefined),
            ...(commentsContext ? { commentsContext } : {}),
            jobProfile,
        };

        return validatingCobrainerApiFetcher.postJson(jobProfileRegenerationResponseSchema, {
            pathname: `/job-profiles/${jobProfile.id}/regenerate`,
            payload: requestPayload,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                timeout: 60_000,
            },
        });
    };

    const deleteFamiliesRegenerationResults: JobArchitectureFetchers['deleteFamiliesRegenerationResults'] = async ({
        jobArchitectureId,
        regenerationId,
    }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${jobArchitectureId}/families/regeneration-results/${regenerationId}`,
        });
    };

    const fetchFamiliesRegenerationResults: JobArchitectureFetchers['fetchFamiliesRegenerationResults'] = async ({
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(familiesRegenerationResultSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/families/regeneration-results`,
        });
    };

    const fetchRegenerationResult: JobArchitectureFetchers['fetchRegenerationResult'] = async ({
        acceptLanguage,
        jobArchitectureId,
        regenerationId,
    }) => {
        return validatingCobrainerApiFetcher.get(jsaRegenerationResultSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/families/regeneration-results/${regenerationId}`,
            requestOptions: { headers: createAcceptLanguageHeaders(acceptLanguage) },
        });
    };

    const regenerateOnboarding: JobArchitectureFetchers['regenerateOnboarding'] = async ({
        acceptLanguage,
        jobArchitectureId,
        jsaDraft,
        prompt,
    }) => {
        return validatingCobrainerApiFetcher.postJson(jsaStructureRegenerationResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/families/regenerate`,
            payload: { currentPreview: jsaDraft, userMessage: prompt },
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                timeout: 60_000,
            },
        });
    };

    const fetchJobArchitectureFilters: JobArchitectureFetchers['fetchJobArchitectureFilters'] = async ({
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureFiltersResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/filter`,
        });
    };

    const fetchJobArchitectureHistory: JobArchitectureFetchers['fetchJobArchitectureHistory'] = async ({
        acceptLanguage,
        jobArchitectureId,
        params,
    }) => {
        const { paged } = params;

        return validatingCobrainerApiFetcher.get(paginatedJobArchitectureHistorySchema, {
            pathname: `/job-architectures/${jobArchitectureId}/history`,
            requestOptions: {
                headers: createAcceptLanguageHeaders(acceptLanguage),
                searchParams: {
                    page: paged.page,
                    size: paged.size,
                    ...(paged.countOnly === true ? { countOnly: paged.countOnly } : {}),
                },
            },
        });
    };

    type SearchFilterConditionPayload =
        | { field: string; ignore_case?: boolean; op: string; value: string }
        | { field: string; op: 'in'; values: readonly string[] }
        | { field: string; op: 'is_null' };

    type SearchFilterPayload = SearchFilterConditionPayload | { filters: SearchFilterConditionPayload[]; op: string };

    type SearchType = 'cluster' | 'family' | 'level' | 'role';

    const buildLevelCondition = (
        type: SearchType | readonly SearchType[] | undefined,
    ): SearchFilterConditionPayload | undefined => {
        if (is.nonEmptyString(type)) {
            return { field: 'level', op: 'eq', value: type };
        }

        if (Array.isArray(type) && type.length > 0) {
            return { field: 'level', op: 'in', values: type };
        }

        return undefined;
    };

    const buildReviewConditions = ({
        reviewerAssignment,
        reviewerId,
        reviewState,
    }: {
        readonly reviewerAssignment?: 'unassigned';
        readonly reviewerId?: string;
        readonly reviewState?: 'approved' | 'assigned';
    }): SearchFilterConditionPayload[] => {
        const conditions: SearchFilterConditionPayload[] = [];

        if (reviewerAssignment === 'unassigned') {
            conditions.push({
                field: 'reviewerId',
                op: 'is_null',
            });
        } else if (is.nonEmptyString(reviewerId)) {
            conditions.push({
                field: 'reviewerId',
                ignore_case: true,
                op: 'eq',
                value: reviewerId,
            });
        }

        if (is.nonEmptyString(reviewState)) {
            conditions.push({
                field: 'reviewState',
                op: 'eq',
                value: reviewState,
            });
        }

        return conditions;
    };

    const buildSearchFilter = (args: {
        readonly reviewerAssignment?: 'unassigned';
        readonly reviewerId?: string;
        readonly reviewState?: 'approved' | 'assigned';
        readonly searchTerm?: string;
        readonly state?: 'active' | 'archived' | 'draft';
        readonly type?: SearchType | readonly SearchType[];
    }): SearchFilterPayload | undefined => {
        const { searchTerm, state, type } = args;

        const filters: SearchFilterConditionPayload[] = [];

        const levelCondition = buildLevelCondition(type);
        if (levelCondition !== undefined) {
            filters.push(levelCondition);
        }

        if (is.nonEmptyString(state)) {
            filters.push({
                field: 'state',
                op: 'eq',
                value: state,
            });
        }

        filters.push(...buildReviewConditions(args));

        if (is.nonEmptyString(searchTerm)) {
            filters.push({
                field: 'title',
                ignore_case: true,
                op: 'contains',
                value: searchTerm,
            });
        }

        if (filters.length === 1) {
            return filters[0]!;
        }

        if (filters.length === 0) {
            return undefined;
        }

        return {
            filters,
            op: 'and',
        };
    };

    const buildSearchPayload = (args: {
        readonly page: number;
        readonly reviewerAssignment?: 'unassigned';
        readonly reviewerId?: string;
        readonly reviewState?: 'approved' | 'assigned';
        readonly searchTerm?: string;
        readonly size: number;
        readonly state?: 'active' | 'archived' | 'draft';
        readonly sort?: {
            readonly direction: 'asc' | 'desc';
            readonly field: string;
        };
        readonly type?: SearchType | readonly SearchType[];
    }) => {
        const filter = buildSearchFilter({
            reviewerAssignment: args.reviewerAssignment,
            reviewerId: args.reviewerId,
            reviewState: args.reviewState,
            searchTerm: args.searchTerm,
            state: args.state,
            type: args.type,
        });

        return {
            ...(filter !== undefined ? { filter } : {}),
            paged: {
                page: args.page,
                size: args.size,
            },
            sorted: {
                fields: [
                    {
                        direction: args.sort?.direction ?? 'asc',
                        field: args.sort?.field ?? 'levelNumber',
                    },
                ],
            },
        };
    };

    const searchJobArchitecture: JobArchitectureFetchers['searchJobArchitecture'] = async ({
        acceptLanguage,
        detail = false,
        jobArchitectureId,
        page = 0,
        reviewerAssignment,
        reviewerId,
        reviewState,
        searchTerm,
        size = 100,
        state,
        sort,
        type,
    }) => {
        return validatingCobrainerApiFetcher.postJson(jobArchitectureSearchResponseWithDetailSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/filter${detail ? '?detail=true' : ''}`,
            payload: buildSearchPayload({
                page,
                reviewerAssignment,
                reviewerId,
                reviewState,
                searchTerm,
                size,
                sort,
                state,
                type,
            }),
            requestOptions: { headers: createAcceptLanguageHeaders(acceptLanguage) },
        });
    };

    const searchJobArchitectures: JobArchitectureFetchers['searchJobArchitectures'] = async ({
        detail = false,
        page = 0,
        reviewerAssignment,
        reviewerId,
        reviewState,
        searchTerm,
        size = 100,
        state,
        sort,
        type,
    }) => {
        return validatingCobrainerApiFetcher.postJson(jobArchitectureSearchResponseWithDetailSchema, {
            pathname: `/job-architectures/filter${detail ? '?detail=true' : ''}`,
            payload: buildSearchPayload({
                page,
                reviewerAssignment,
                reviewerId,
                reviewState,
                searchTerm,
                size,
                sort,
                state,
                type,
            }),
        });
    };

    const fetchLevelingFrameworks: JobArchitectureFetchers['fetchLevelingFrameworks'] = async ({ params }) => {
        const { paged } = params;

        return validatingCobrainerApiFetcher.get(paginatedLevelingFrameworkSchema, {
            pathname: '/leveling-frameworks',
            requestOptions: {
                searchParams: {
                    countOnly: paged.countOnly,
                    page: paged.page,
                    size: paged.size,
                },
            },
        });
    };

    const createLevelingFramework: JobArchitectureFetchers['createLevelingFramework'] = async ({ payload }) => {
        return validatingCobrainerApiFetcher.postJson(levelingFrameworkSchema, {
            pathname: '/leveling-frameworks',
            payload,
        });
    };

    const fetchLevelingFramework: JobArchitectureFetchers['fetchLevelingFramework'] = async ({ frameworkId }) => {
        return validatingCobrainerApiFetcher.get(levelingFrameworkSchema, {
            pathname: `/leveling-frameworks/${frameworkId}`,
        });
    };

    const updateLevelingFramework: JobArchitectureFetchers['updateLevelingFramework'] = async ({
        frameworkId,
        payload,
    }) => {
        return validatingCobrainerApiFetcher.putJson(levelingFrameworkSchema, {
            pathname: `/leveling-frameworks/${frameworkId}`,
            payload,
        });
    };

    const deleteLevelingFramework: JobArchitectureFetchers['deleteLevelingFramework'] = async ({ frameworkId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/leveling-frameworks/${frameworkId}`,
        });
    };

    const fetchJobArchitectureLevelingFrameworks: JobArchitectureFetchers['fetchJobArchitectureLevelingFrameworks'] =
        async ({ jobArchitectureId, params }) => {
            const { paged } = params;

            return validatingCobrainerApiFetcher.get(paginatedLevelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks`,
                requestOptions: {
                    searchParams: {
                        countOnly: paged.countOnly,
                        page: paged.page,
                        size: paged.size,
                    },
                },
            });
        };

    const linkJobArchitectureLevelingFramework: JobArchitectureFetchers['linkJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId }) => {
            return validatingCobrainerApiFetcher.post(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}/use`,
            });
        };

    const createJobArchitectureLevelingFramework: JobArchitectureFetchers['createJobArchitectureLevelingFramework'] =
        async ({ jobArchitectureId, payload }) => {
            return validatingCobrainerApiFetcher.postJson(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks`,
                payload,
            });
        };

    const fetchJobArchitectureLevelingFramework: JobArchitectureFetchers['fetchJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId }) => {
            return validatingCobrainerApiFetcher.get(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
            });
        };

    const updateJobArchitectureLevelingFramework: JobArchitectureFetchers['updateJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId, payload }) => {
            return validatingCobrainerApiFetcher.putJson(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
                payload,
            });
        };

    const deleteJobArchitectureLevelingFramework: JobArchitectureFetchers['deleteJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId }) => {
            return cobrainerApiFetcher.delete({
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
            });
        };

    const deleteJobArchitecture: JobArchitectureFetchers['deleteJobArchitecture'] = async ({ jobArchitectureId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${jobArchitectureId}`,
        });
    };

    const assignDefaultLevelingFramework: JobArchitectureFetchers['assignDefaultLevelingFramework'] = async ({
        jobArchitectureId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/assign-default`,
        });
    };

    const archiveJobLevel: JobArchitectureFetchers['archiveJobLevel'] = async ({ jobArchitectureId, jobLevelId }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/archive`,
        });
    };

    const archiveJobLevels: JobArchitectureFetchers['archiveJobLevels'] = async ({
        jobArchitectureId,
        jobLevelIds,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/archive`,
            payload: jobLevelIds,
        });
    };

    const archiveJobRole: JobArchitectureFetchers['archiveJobRole'] = async ({ jobArchitectureId, jobRoleId }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/archive`,
        });
    };

    const archiveJobRoles: JobArchitectureFetchers['archiveJobRoles'] = async ({ jobArchitectureId, jobRoleIds }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/archive`,
            payload: jobRoleIds,
        });
    };

    const fetchArchivedJobLevels: JobArchitectureFetchers['fetchArchivedJobLevels'] = async ({
        includeProfiles = true,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/levels/archived`,
            requestOptions: {
                searchParams: {
                    includeProfiles,
                },
            },
        });
    };

    const fetchArchivedJobRoles: JobArchitectureFetchers['fetchArchivedJobRoles'] = async ({
        includeProfiles = true,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/roles/archived`,
            requestOptions: {
                searchParams: {
                    includeProfiles,
                },
            },
        });
    };

    const unarchiveJobLevel: JobArchitectureFetchers['unarchiveJobLevel'] = async ({
        jobArchitectureId,
        jobLevelId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/unarchive`,
        });
    };

    const unarchiveJobLevels: JobArchitectureFetchers['unarchiveJobLevels'] = async ({
        jobArchitectureId,
        jobLevelIds,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/unarchive`,
            payload: jobLevelIds,
        });
    };

    const inviteAndAssignToLevel: JobArchitectureFetchers['inviteAndAssignToLevel'] = async ({
        jobArchitectureId,
        levelId,
        request,
    }) => {
        const { inviteLanguage, ...payload } = request;

        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${levelId}/invite-and-assign`,
            payload,
            requestOptions: {
                searchParams: new URLSearchParams(inviteLanguage !== undefined ? { inviteLanguage } : {}),
            },
        });
    };

    const inviteAndAssignToRole: JobArchitectureFetchers['inviteAndAssignToRole'] = async ({
        jobArchitectureId,
        request,
        roleId,
    }) => {
        const { inviteLanguage, ...payload } = request;

        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${roleId}/invite-and-assign`,
            payload,
            requestOptions: {
                searchParams: new URLSearchParams(inviteLanguage !== undefined ? { inviteLanguage } : {}),
            },
        });
    };

    const unarchiveJobRole: JobArchitectureFetchers['unarchiveJobRole'] = async ({ jobArchitectureId, jobRoleId }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/unarchive`,
        });
    };

    const unarchiveJobRoles: JobArchitectureFetchers['unarchiveJobRoles'] = async ({
        jobArchitectureId,
        jobRoleIds,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/unarchive`,
            payload: jobRoleIds,
        });
    };

    return {
        addCareerTrack,
        archiveJobLevel,
        archiveJobLevels,
        archiveJobRole,
        archiveJobRoles,
        assignDefaultLevelingFramework,
        assignReviewer,
        bulkApplyOnboardingDraft,
        copyJobClusters,
        copyJobLevels,
        copyJobRoles,
        createJobArchitecture,
        createJobArchitectureLevelingFramework,
        createLevelingFramework,
        createSkillCatalogEntry,
        deleteFamiliesRegenerationResults,
        deleteJobArchitecture,
        deleteJobArchitectureLevelingFramework,
        deleteJobClusters,
        deleteJobFamilies,
        deleteJobLevels,
        deleteJobRoles,
        deleteLevelingFramework,
        deleteSkillCatalogEntry,
        editJobLevelVersionNote,
        editJobRoleVersionNote,
        exportJobArchitecture,
        exportJobLevel,
        exportJobRole,
        requestChangelogExport,
        requestSkillCatalogExport,
        fetchArchivedJobLevels,
        fetchArchivedJobRoles,
        fetchAssignableReviewers,
        fetchCareerTracks,
        fetchCompanyInformationGenerationStatus,
        fetchLevelingFrameworkAssessment,
        fetchLevelingFrameworkGeneration,
        fetchCompetencies,
        fetchCommentStatistics,
        fetchContributionTimeline,
        fetchDebugInformation,
        fetchFamiliesWithClusters,
        fetchJobArchitecture,
        fetchJobArchitectureFilters,
        fetchJobArchitectureHistory,
        fetchJobArchitectureInflight,
        fetchJobArchitectureLevelingFramework,
        fetchJobArchitectureLevelingFrameworks,
        fetchJobArchitectureMetadata,
        fetchJobArchitectureOnboardingState,
        fetchJobArchitectureOptions,
        fetchJobArchitectures,
        fetchJobArchitectureSkills,
        fetchJobClusters,
        fetchJobFamilies,
        fetchJobLevels,
        fetchJobLevelVersions,
        fetchJobRoles,
        fetchJobRoleVersions,
        fetchFamiliesRegenerationResults,
        fetchRegenerationResult,
        fetchLevelingFramework,
        fetchLevelingFrameworks,
        fetchRelatedSkills,
        fetchSkillCatalogOptions,
        fetchSkillCatalogStatistics,
        fetchSkillDetails,
        fetchSkillLimits,
        fetchSkillLocales,
        fetchSkillReferences,
        fetchSkillsBySearchTerm,
        fetchTaskManagementClusters,
        searchTaskManagementClusterTasks,
        fetchTaskManagementTask,
        fetchWorkTaskAutomationRecommendation,
        searchTaskManagementTasks,
        cancelJobArchitectureGenerations,
        generateJobArchitectureWithAI,
        generateJobClustersWithAI,
        generateJobFamilyWithAI,
        generateJobLevelsWithAI,
        generateJobLevelsForAllRoles,
        generateJobRolesWithAI,
        getJobArchitectureItemPath,
        getUsers,
        inviteAndAssignToLevel,
        inviteAndAssignToRole,
        linkJobArchitectureLevelingFramework,
        moveJobClusters,
        moveJobLevels,
        moveJobRoles,
        patchJobArchitecture,
        patchJobArchitectureMetadata,
        patchJobArchitectureOnboardingState,
        patchJobCluster,
        patchJobFamily,
        patchJobLevel,
        patchJobRole,
        persistJobCluster,
        persistJobFamily,
        persistJobLevel,
        persistJobRole,
        publishJobLevelVersion,
        publishJobRoleVersion,
        unpublishJobLevelVersion,
        unpublishJobRoleVersion,
        putJobCluster,
        putJobFamily,
        readJobLevel,
        readJobLevelDetails,
        readJobRole,
        readJobRoleDetails,
        redetectSkills,
        regenerateJobProfileDescription,
        regenerateOnboarding,
        reorderJobClusters,
        reorderJobFamilies,
        reorderJobLevels,
        reorderJobRoles,
        reviewJobLevelVersion,
        reviewJobRoleVersion,
        searchJobArchitecture,
        searchJobArchitectures,
        submitCompanyInformationGeneration,
        submitLevelingFrameworkAssessment,
        submitLevelingFrameworkGeneration,
        unarchiveJobLevel,
        unarchiveJobLevels,
        unarchiveJobRole,
        unarchiveJobRoles,
        unassignReviewer,
        unreviewJobLevelVersion,
        unreviewJobRoleVersion,
        updateJobArchitectureLevelingFramework,
        updateJobItemProfile,
        updateJobProfile,
        updateLevelingFramework,
        updateSkillCatalogEntry,
        fetchManagedJobProfileTemplates,
        fetchCustomJobProfileTemplate,
        putCustomJobProfileTemplate,
        deleteCustomJobProfileTemplate,
        postJobProfileTemplatePreview,
    };
};
